
import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { motion } from 'framer-motion';
import { toast } from '@/hooks/use-toast';

interface SuccessStateProps {
  error?: string;
}

const SuccessState: React.FC<SuccessStateProps> = ({ error }) => {
  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error || "Something went wrong. Please try again.",
      });
    } else {
      toast({
        title: "Success!",
        description: "Taking you to your results...",
        className: "bg-green-50 border-green-200",
      });
    }
  }, [error]);

  if (error) {
    return (
      <div className="text-center py-4">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error || "Something went wrong. Please try again."}
          </AlertDescription>
        </Alert>
        <p className="mt-4 text-sm text-gray-500">
          You may continue to your results, but your data won't be saved to an account.
        </p>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="text-center py-4"
    >
      <div className="flex flex-col items-center justify-center">
        <motion.div 
          className="rounded-full bg-green-100 p-3 mb-4"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ 
            duration: 0.5,
            type: "spring",
            stiffness: 200
          }}
        >
          <CheckCircle className="h-8 w-8 text-green-600" />
        </motion.div>
        <h3 className="text-xl font-medium text-gray-900 mb-2">Success!</h3>
        <p className="text-sm text-gray-500">Taking you to your results...</p>
        <div className="mt-3 w-8 h-8 border-4 border-morgenBlue border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    </motion.div>
  );
};

export default SuccessState;
